import decodePolyLine from "../map/DecodePolyLine";
import {metersToMiles} from "../../utils/General";
import {RouteWithEtasPoint} from "../../generated/graphql";
import {RowData} from "./types/types";

export interface RouteWithEtasStopCoordinate {
  lat: number;
  lon: number;
}

export interface GetRouteWithEtasVariables {
  routePoints: RouteWithEtasPoint[];
  startDateTime?: string;
  dwellTimeSeconds?: number;
  service: string;
}

export interface RouteWithEtasResult {
  path: {lat: number; lng: number}[];
  miles: number;
  etaByJobStopId: Map<number, string>;
  etaSourceByJobStopId: Map<number, string>;
  endDateTime?: string;
  credits: number;
}

export interface RouteEtasUpdate {
  etaByJobStopId: Map<number, string>;
  etaSourceByJobStopId: Map<number, string>;
  endDateTime?: string;
  miles: number;
}

export interface RouteWithEtasEta {
  rateLimitCredits?: number | null;
  distance?: number | null;
  drivingTimeSeconds?: number | null;
  items?: ({jobStopId?: number | null; eta?: string | null; etaSource?: string | null} | null)[] | null;
  polyline?: {coordinates?: (number[] | null)[] | null; encodedLine?: string | null} | null;
}

export const buildRouteWithEtasVariables = (
  row: RowData,
  stopCoordinates: Map<number, RouteWithEtasStopCoordinate>,
  dwellSeconds: number
): GetRouteWithEtasVariables | null => {
  if (row.startLat == null || row.startLng == null || row.driverId == null) return null;

  const routePoints: RouteWithEtasPoint[] = [{id: "start", coordinates: [row.startLng, row.startLat]}];
  let hasStops = false;
  row.assignedStops.forEach((stop) => {
    // DP-3202: Skip completed stops — they shouldn't be included in route calculations
    if (stop.jobStopStatus === "C") return;
    const coord = stopCoordinates.get(stop.jobStopId);
    if (coord) {
      routePoints.push({
        id: String(stop.jobStopId),
        coordinates: [coord.lon, coord.lat],
        earlyDateTime: stop.earlyDateTime ?? undefined,
        scheduledDateTime: stop.scheduledDateTime ?? undefined,
        stopType: stop.stopType ?? undefined
      });
      hasStops = true;
    }
  });

  if (!hasStops) return null;

  if (row.endLat != null && row.endLng != null) {
    routePoints.push({id: "end", coordinates: [row.endLng, row.endLat]});
  }

  const startDateTime =
    row.startDateTime ?? (row.startTime instanceof Date ? row.startTime.toISOString() : new Date().toISOString());

  return {routePoints, startDateTime, dwellTimeSeconds: dwellSeconds, service: "route-planning"};
};

export const parseRouteWithEtasResult = (
  eta: RouteWithEtasEta | null | undefined,
  dwellSeconds = 0
): RouteWithEtasResult | null => {
  if (!eta) return null;

  const etaByJobStopId = new Map<number, string>();
  const etaSourceByJobStopId = new Map<number, string>();
  (eta.items ?? []).forEach((item) => {
    if (item?.jobStopId != null && item.eta) {
      etaByJobStopId.set(item.jobStopId, item.eta);
      if (item.etaSource) {
        etaSourceByJobStopId.set(item.jobStopId, item.etaSource);
      }
    }
  });

  let path: {lat: number; lng: number}[] = [];
  if (eta.polyline?.encodedLine) {
    path = decodePolyLine(eta.polyline.encodedLine);
  } else if (eta.polyline?.coordinates) {
    path = eta.polyline.coordinates
      .filter((c): c is number[] => Array.isArray(c) && c.length >= 2)
      .map((c) => ({lat: c[1], lng: c[0]}));
  }

  // DP-3099 B01: The backend may include an end-location item (jobStopId == null)
  // as the last entry. Its ETA already accounts for all dwell + travel to end.
  const items = eta.items ?? [];
  const lastItem = items.length > 0 ? items[items.length - 1] : null;
  let endDateTime: string | undefined;
  if (lastItem?.jobStopId == null && lastItem?.eta) {
    // End-location item — use directly (dwell + travel already included)
    endDateTime = lastItem.eta;
  } else {
    // Fallback: last stop ETA + dwell (no end-location travel time available)
    const lastStopEta = lastItem?.eta ?? undefined;
    endDateTime = lastStopEta
      ? new Date(new Date(lastStopEta).getTime() + dwellSeconds * 1000).toISOString()
      : undefined;
  }

  return {
    path,
    miles: metersToMiles(eta.distance ?? 0),
    etaByJobStopId,
    etaSourceByJobStopId,
    endDateTime,
    credits: eta.rateLimitCredits ?? 0
  };
};
